import type { SelectionAnchorRect } from "@cohesive/ui-components";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

type UseDomTextSelectionActionOptions = {
  enabled?: boolean;
  /** In-page scroll container — selection must fall inside this element. */
  rootRef: RefObject<HTMLElement | null>;
};

function selectionWithinRoot(
  root: HTMLElement,
  sel: Selection | null,
): boolean {
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  return root.contains(range.commonAncestorContainer);
}

function readSelectionAnchorInRoot(
  sel: Selection,
): { selectedText: string; anchorRect: SelectionAnchorRect } | null {
  const text = sel.toString().trim();
  if (!text) return null;

  try {
    const r = sel.getRangeAt(0).getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return {
      selectedText: text,
      anchorRect: {
        left: r.left,
        top: r.top,
        width: r.width,
        height: r.height,
      },
    };
  } catch {
    return null;
  }
}

/** Tracks non-empty text selection inside a scroll container for the reference pill. */
export function useDomTextSelectionAction({
  enabled = true,
  rootRef,
}: UseDomTextSelectionActionOptions) {
  const [selectedText, setSelectedText] = useState("");
  const [anchorRect, setAnchorRect] = useState<SelectionAnchorRect | null>(
    null,
  );
  const pointerSelectingRef = useRef(false);

  const clearSelection = useCallback(() => {
    setSelectedText("");
    setAnchorRect(null);
    pointerSelectingRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearSelection();
      return;
    }

    const root = rootRef.current;
    if (!root) return;

    const applySelectionUi = () => {
      const sel = document.getSelection();
      if (!selectionWithinRoot(root, sel)) {
        setSelectedText("");
        setAnchorRect(null);
        return;
      }
      const result = readSelectionAnchorInRoot(sel!);
      if (result) {
        setSelectedText(result.selectedText);
        setAnchorRect(result.anchorRect);
      } else {
        setSelectedText("");
        setAnchorRect(null);
      }
    };

    const handleSelectionChange = () => {
      if (pointerSelectingRef.current) {
        setSelectedText("");
        setAnchorRect(null);
        return;
      }
      applySelectionUi();
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (!root.contains(e.target as Node)) return;
      pointerSelectingRef.current = true;
      setSelectedText("");
      setAnchorRect(null);
    };

    const handlePointerUp = () => {
      if (!pointerSelectingRef.current) return;
      pointerSelectingRef.current = false;
      queueMicrotask(() => {
        applySelectionUi();
      });
    };

    const handleScroll = () => {
      if (pointerSelectingRef.current) return;
      applySelectionUi();
    };

    const scrollListenerOptions: AddEventListenerOptions = {
      capture: true,
      passive: true,
    };

    root.addEventListener("pointerdown", handlePointerDown);
    root.addEventListener("scroll", handleScroll, scrollListenerOptions);
    document.addEventListener("selectionchange", handleSelectionChange);
    window.addEventListener("pointerup", handlePointerUp, true);
    window.addEventListener("scroll", handleScroll, scrollListenerOptions);

    return () => {
      root.removeEventListener("pointerdown", handlePointerDown);
      root.removeEventListener("scroll", handleScroll, scrollListenerOptions);
      document.removeEventListener("selectionchange", handleSelectionChange);
      window.removeEventListener("pointerup", handlePointerUp, true);
      window.removeEventListener("scroll", handleScroll, scrollListenerOptions);
      pointerSelectingRef.current = false;
    };
  }, [enabled, rootRef, clearSelection]);

  return { selectedText, anchorRect, clearSelection };
}

export type IframeTextSelectionState = {
  selectedText: string;
  anchorRect: SelectionAnchorRect | null;
};

/** Same-origin iframe selection listeners (HTML file records). Returns cleanup fn. */
export function attachIframeTextSelectionListeners(params: {
  iframeEl: HTMLIFrameElement;
  enabled: boolean;
  onSelectionChange: (state: IframeTextSelectionState) => void;
}): () => void {
  const { iframeEl, enabled, onSelectionChange } = params;
  const empty = (): IframeTextSelectionState => ({
    selectedText: "",
    anchorRect: null,
  });

  if (!enabled) {
    onSelectionChange(empty());
    return () => {};
  }

  const doc =
    iframeEl.contentDocument ?? iframeEl.contentWindow?.document ?? null;
  if (!doc) {
    onSelectionChange(empty());
    return () => {};
  }

  let pointerSelecting = false;

  const applyIframeSelectionUi = () => {
    const sel = doc.getSelection();
    const text = sel?.toString().trim() ?? "";
    const iframeRect = iframeEl.getBoundingClientRect();
    if (text && sel && sel.rangeCount > 0) {
      try {
        const r = sel.getRangeAt(0).getBoundingClientRect();
        onSelectionChange({
          selectedText: text,
          anchorRect: {
            left: iframeRect.left + r.left,
            top: iframeRect.top + r.top,
            width: r.width,
            height: r.height,
          },
        });
        return;
      } catch {
        // fall through
      }
    }
    onSelectionChange(empty());
  };

  const handleSelectionChange = () => {
    if (pointerSelecting) {
      onSelectionChange(empty());
      return;
    }
    applyIframeSelectionUi();
  };

  const handleIframePointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    pointerSelecting = true;
    onSelectionChange(empty());
  };

  const handleIframePointerUp = () => {
    if (!pointerSelecting) return;
    pointerSelecting = false;
    queueMicrotask(() => {
      applyIframeSelectionUi();
    });
  };

  const handleParentWindowPointerUp = () => {
    handleIframePointerUp();
  };

  const handleScroll = () => {
    if (pointerSelecting) return;
    applyIframeSelectionUi();
  };

  const scrollListenerOptions: AddEventListenerOptions = {
    capture: true,
    passive: true,
  };

  doc.addEventListener("pointerdown", handleIframePointerDown);
  doc.addEventListener("pointerup", handleIframePointerUp, true);
  doc.addEventListener("scroll", handleScroll, scrollListenerOptions);
  window.addEventListener("pointerup", handleParentWindowPointerUp, true);
  window.addEventListener("scroll", handleScroll, scrollListenerOptions);
  doc.addEventListener("selectionchange", handleSelectionChange);

  return () => {
    try {
      doc.removeEventListener("pointerdown", handleIframePointerDown);
      doc.removeEventListener("pointerup", handleIframePointerUp, true);
      doc.removeEventListener("scroll", handleScroll, scrollListenerOptions);
      window.removeEventListener(
        "pointerup",
        handleParentWindowPointerUp,
        true,
      );
      window.removeEventListener("scroll", handleScroll, scrollListenerOptions);
      doc.removeEventListener("selectionchange", handleSelectionChange);
      pointerSelecting = false;
    } catch {
      // iframe may already be torn down
    }
    onSelectionChange(empty());
  };
}
